import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { makeStyles } from 'tss-react/mui';
import {
	useTheme,
	ListItemText,
	useMediaQuery,
	TableHead,
	TableRow,
	TableCell,
	Button,
	CircularProgress,
	Box,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	LinearProgress,
	IconButton,
	Tooltip,
	Typography,
	TablePagination,
} from '@mui/material';

// import { Face as Avatar } from '@mui/icons-material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SavingsIcon from '@mui/icons-material/Savings';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

import {
	FetchUserProps,
	OnBoardingStatus,
	fetchUserByDocument,
	fetchUserByQRCode,
	inviteUsers,
	inactivateUser,
	activateUser,
} from 'lib/models/users';
import { enqueueSnackbarError } from 'lib/helpers';

import ResponsiveTable from 'components/ResponsiveTable';
import FullLayout from 'components/layouts/FullLayout';
import { CustomTooltipProps } from 'components/tooltips/CustomTooltip/CustomTooltip';
import SearchToolbar from 'components/SearchToolbar';
import ConfirmationDialog from 'components/ConfirmationDialog';

import { rowsPerPageOptions } from 'config/constants';
import { UsersTypes } from 'types/global';
import { useNavigate } from 'react-router';
import moment from 'moment';
import useLoggedUser from 'hooks/useLoggedUser';
import useIsAppUsers from 'hooks/useIsAppUsers';
import useUsers from 'hooks/useUsers';
import useUserLastOrder from 'hooks/useUserLastOrder';
import useUserWalletStatus from 'hooks/useUserWalletStatus';
import useUserRefId from 'hooks/useUserRefId';
import CardHolderId from './CardHolderId';
import SelectUserSectionModal from 'components/SelectUserSectionModal';
import SectionButton from './SectionButton';
import { nanoid } from '@reduxjs/toolkit';
import UserBalance from './UserBalance';
import QrScanModal from 'components/QrScanModal';
import useCardHolderIdEnabled from './useCardHolderIdEnabled';

type Props = {
	editUrl?: string;
	paper?: boolean;
	disabled?: boolean;
	customAction?: CustomTooltipProps;
};

const userBalanceAdminTypes: UserType[] = ['admin', 'userBalanceAdmin'];

// Array of supported user types:
const supportedTypes = {
	// If logged user type is 'admin', it can list all users
	[UsersTypes.admin]: '*',
	// If logged user type is parking admin (only show 'parking users')
	[UsersTypes.parkingAdmin]: [UsersTypes.parkingCashier, UsersTypes.parkingGate] as UserType[],
	// If logged user type is bar admin (only show 'bar users')
	[UsersTypes.barAdmin]: [UsersTypes.barCashier] as UserType[],
};

export default function UserTable(props: Props) {
	const { t } = useTranslation();

	const { classes } = useStyles();

	const navigate = useNavigate();

	const isAppUsers = useIsAppUsers();

	const { loading: loadingEnabled, enabled } = useCardHolderIdEnabled();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [searchParams, setSearchParams] = useSearchParams({
		search: '',
		page: '0',
		rowsPerPage: '25',
		type: '',
	});

	const [search, setSearch] = useState(searchParams.get('search') || '');
	const [type, setType] = useState(searchParams.get('type') || '');
	const [page, setPage] = useState(Number(searchParams.get('page') || 0));
	const [rowsPerPage, setRowsPerPage] = useState(Number(searchParams.get('rowsPerPage') || 25));
	const [documentSearch, setDocumentSearch] = useState('');
	const [documentUser, setDocumentUser] = useState<User | null>(null);
	const [documentLoading, setDocumentLoading] = useState(false);
	const [documentSearchKey, setDocumentSearchKey] = useState(0); // Key to force SearchToolbar remount
	const [showQrScanner, setShowQrScanner] = useState(false);
	const [qrUser, setQrUser] = useState<User | null>(null);
	const [fetchWalletStatus, setFetchWalletStatus] = useState<string>('');
	const [fetchRefID, setFetchRefID] = useState<string>('');
	const [lastOrderRefreshUser, setLastOrderRefreshUser] = useState<string>('');
	const [lastOrderRefreshKey, setLastOrderRefreshKey] = useState(0);
	const [userToToggleStatus, setUserToToggleStatus] = useState<User | null>(null);
	const [inactivateLoading, setInactivateLoading] = useState(false);

	const searchProps = useMemo(() => {
		let params: FetchUserProps = { page, rowsPerPage, search };
		if (type) {
			params = { ...params, type: type as UserType };
		}
		return params;
	}, [page, rowsPerPage, search, type]);

	const { loading, users: usersData, refetch } = useUsers(searchProps);

	const [sectionId, setSection] = useState(nanoid());

	const [cardHolderIdEdition, setCardHolderIdEdition] = useState<User | null>(null);
	const [userBalance, setUserBalance] = useState<User | null>(null);
	const [showSelectUserSectionModal, setShowSelectUserSectionModal] = useState<User | null>(null);

	const closeSelectUserSectionModal = () => {
		setShowSelectUserSectionModal(null);
		setSection(nanoid());
	};

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	function onSearch(value: string) {
		setSearch(value);
		setPage(0);
		// Clear document search when regular search is used
		setDocumentUser(null);
		setDocumentSearch('');
		setQrUser(null);
	}

	async function onDocumentSearch(value: string) {
		setDocumentSearch(value);
		if (!value || value.trim().length === 0) {
			setDocumentUser(null);
			return;
		}
		try {
			setDocumentLoading(true);
			const user = await fetchUserByDocument(value.trim());
			setDocumentLoading(false);

			if (!user) {
				// Show error message when user is not found
				enqueueSnackbarError('Documento no encontrado');
				setDocumentUser(null);
				setDocumentSearch('');
				setDocumentSearchKey((prev) => prev + 1); // Force remount to clear chip
			} else {
				setDocumentUser(user);
				// Clear regular search when document search is used
				setSearch('');
				setQrUser(null);
				setPage(0);
			}
		} catch (error) {
			console.error('Error searching by document:', error);
			enqueueSnackbarError(error);
			setDocumentUser(null);
			setDocumentSearch('');
			setDocumentSearchKey((prev) => prev + 1); // Force remount to clear chip
			setDocumentLoading(false);
		}
	}

	async function onQrCodeScan(qrData?: string) {
		setShowQrScanner(false);
		if (!qrData) {
			return;
		}
		try {
			setDocumentLoading(true);
			const user = await fetchUserByQRCode(qrData);
			setDocumentLoading(false);

			if (!user) {
				enqueueSnackbarError('Usuario no encontrado con el código QR escaneado');
				setQrUser(null);
			} else {
				setQrUser(user);
				// Clear other search methods when QR search is used
				setSearch('');
				setDocumentUser(null);
				setDocumentSearch('');
				setPage(0);
			}
		} catch (error) {
			console.error('Error searching by QR code:', error);
			enqueueSnackbarError(error);
			setQrUser(null);
			setDocumentLoading(false);
		}
	}

	useEffect(() => {
		setSearchParams({
			page: `${page}`,
			rowsPerPage: `${rowsPerPage}`,
			search: `${search}`,
			type: `${type}`,
		});
	}, [setSearchParams, page, rowsPerPage, search, type]);

	const logged = useLoggedUser();
	const isAdmin = logged?.type === UsersTypes.admin;

	const users = useMemo(() => {
		// If we have a QR search result, show only that user
		if (qrUser) {
			const adminFilter = qrUser.username !== 'admin';
			const loggedFilter = qrUser._id !== logged?._id;
			const loggedType = logged?.type as keyof typeof supportedTypes | undefined;
			const rolesFilter =
				!!loggedType && (supportedTypes[loggedType] === '*' || supportedTypes[loggedType]?.includes(qrUser.type));
			return adminFilter && loggedFilter && rolesFilter ? [qrUser] : [];
		}

		// If we have a document search result, show only that user
		if (documentUser) {
			const adminFilter = documentUser.username !== 'admin';
			const loggedFilter = documentUser._id !== logged?._id;
			const loggedType = logged?.type as keyof typeof supportedTypes | undefined;
			const rolesFilter =
				!!loggedType && (supportedTypes[loggedType] === '*' || supportedTypes[loggedType]?.includes(documentUser.type));
			return adminFilter && loggedFilter && rolesFilter ? [documentUser] : [];
		}

		// Otherwise, use regular filtered users
		if (!usersData?.elements) return [];
		return usersData.elements.filter((user) => {
			// Do not show the super admin user (can't be deleted or edited)
			const adminFilter = user.username !== 'admin';
			// Do not show logged user (Logged user is edited from the profile screen)
			const loggedFilter = user._id !== logged?._id;
			// Filter by user type
			const loggedType = logged?.type as keyof typeof supportedTypes | undefined;
			// Validate supported types
			const rolesFilter =
				!!loggedType && (supportedTypes[loggedType] === '*' || supportedTypes[loggedType]?.includes(user.type));
			// Return filter result
			return adminFilter && loggedFilter && rolesFilter;
		});
	}, [logged?._id, logged?.type, usersData?.elements, documentUser, qrUser]);

	const isBalanceAdmin = userBalanceAdminTypes.includes(logged?.type ?? ('' as UserType));

	function onEdit(selected: User) {
		const { _id } = selected;
		navigate(`/panel/${props.editUrl || 'users'}/${_id}`);
	}

	const renderUserStatus = useCallback(
		(user: User) => {
			if (user.type !== UsersTypes.admin && user.type !== UsersTypes.panel) {
				return <UserStatus user={user} refetch={refetch} />;
			}
			return <span className={classes.status}>{t(`common:${user.status}`)}</span>;
		},
		[classes.status, refetch, t],
	);

	const renderUserActions = useCallback(
		(user: User, direction: 'row' | 'column', showAppRefresh = false) => (
			<Box sx={{ display: 'flex', flexDirection: direction, gap: 1, justifyContent: 'center', alignItems: 'center' }}>
				{!isAppUsers && (
					<Tooltip title={t(`common:edit`)}>
						<span>
							<IconButton onClick={() => onEdit(user)}>
								<EditIcon />
							</IconButton>
						</span>
					</Tooltip>
				)}
				{/* Card Holder ID edition is only present in dev and staging */}
				{isAppUsers && !loadingEnabled && enabled && (
					<Tooltip title="CardHolderId">
						<span>
							<IconButton onClick={() => setCardHolderIdEdition(user)}>
								<CreditCardIcon />
							</IconButton>
						</span>
					</Tooltip>
				)}

				{/* Card Holder ID edition is only present in dev and staging */}
				{isAppUsers && isBalanceAdmin && (
					<Tooltip title="Saldo cortesía">
						<span>
							<IconButton onClick={() => setUserBalance(user)}>
								<SavingsIcon />
							</IconButton>
						</span>
					</Tooltip>
				)}

				{/* Card Holder ID edition is only present in dev and staging */}
				{isAdmin && (
					<Tooltip title={user.status === 'inactive' ? 'Activar' : 'Inactivar'}>
						<span>
							<IconButton
								onClick={() => setUserToToggleStatus(user)}
								color={user.status === 'inactive' ? 'success' : 'warning'}
								disabled={inactivateLoading}
							>
								{user.status === 'inactive' ? <CheckCircleOutlineIcon /> : <BlockIcon />}
							</IconButton>
						</span>
					</Tooltip>
				)}

				{showAppRefresh && isAppUsers && (
					<Tooltip title="Actualizar última compra">
						<span>
							<IconButton
								size="small"
								onClick={() => {
									setLastOrderRefreshUser(user.username);
									setLastOrderRefreshKey((prev) => prev + 1);
								}}
							>
								<RefreshIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				)}
				{showAppRefresh && isAppUsers && (
					<Tooltip title="Actualizar estado billetera">
						<span>
							<IconButton size="small" onClick={() => setFetchWalletStatus(user.username)}>
								<AccountBalanceWalletOutlinedIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				)}
				{showAppRefresh && isAppUsers && (
					<Tooltip title="Actualizar REF ID">
						<span>
							<IconButton size="small" onClick={() => setFetchRefID(user.username)}>
								<FingerprintIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				)}

				<SectionButton key={sectionId} onPress={() => setShowSelectUserSectionModal(user)} user={user} />
			</Box>
		),
		[enabled, inactivateLoading, isAdmin, isAppUsers, isBalanceAdmin, loadingEnabled, onEdit, sectionId, t],
	);

	const onToggleStatusConfirm = useCallback(
		async (confirmed: boolean) => {
			const targetUser = userToToggleStatus;
			setUserToToggleStatus(null);
			if (!confirmed || !targetUser) {
				return;
			}
			try {
				setInactivateLoading(true);
				if (targetUser.status === 'inactive') {
					await activateUser(targetUser);
				} else {
					await inactivateUser(targetUser);
				}
				refetch?.();
			} catch (error) {
				enqueueSnackbarError(error);
			}
			setInactivateLoading(false);
		},
		[refetch, userToToggleStatus],
	);

	return (
		<FullLayout paper={props.paper}>
			{!!loading && <LinearProgress />}

			<Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 2 }}>
				{!isAppUsers && (
					<FormControl size="small" sx={{ minWidth: '15vw' }}>
						<InputLabel>Tipo de usuario</InputLabel>
						<Select
							labelId="type"
							id="type"
							label="Tipo de usuario"
							value={type}
							onChange={(ev) => {
								setType(ev.target.value as string);
								setPage(0);
							}}
						>
							<MenuItem value="">Todos</MenuItem>
							<MenuItem value="admin">{t('common:role_admin')}</MenuItem>
							<MenuItem value="panel">{t('common:role_panel')}</MenuItem>
							<MenuItem value="parkingAdmin">{t('common:role_parkingAdmin')}</MenuItem>
							<MenuItem value="parkingGate">{t('common:role_parkingGate')}</MenuItem>
							<MenuItem value="barAdmin">{t('common:role_barAdmin')}</MenuItem>
							<MenuItem value="barPanel">{t('common:role_barPanel')}</MenuItem>
							<MenuItem value="barCashier">{t('common:role_barCashier')}</MenuItem>
							<MenuItem value="barGuest">{t('common:role_barGuest')}</MenuItem>
							<MenuItem value="qrGenerator">{t('common:role_qrGenerator')}</MenuItem>
							<MenuItem value="inAppSaleAdmin">{t('common:role_inAppSaleAdmin')}</MenuItem>
							<MenuItem value="inAppSaleAdmin">{t('common:role_report')}</MenuItem>
							<MenuItem value="nfcAdmin">{t('common:role_nfcAdmin')}</MenuItem>
						</Select>
					</FormControl>
				)}
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<SearchToolbar
						onSearch={onSearch}
						initialValue={search}
						disabled={props.disabled || !!documentUser || !!qrUser}
						customAction={props.customAction}
					/>
				</Box>
				{isAppUsers && (
					<Box
						sx={{ position: 'relative', minWidth: isMobile ? '100%' : '250px', maxWidth: isMobile ? '100%' : '250px' }}
					>
						{documentLoading && (
							<LinearProgress
								sx={{
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									zIndex: 1,
								}}
							/>
						)}
						<SearchToolbar
							key={documentSearchKey}
							onSearch={onDocumentSearch}
							initialValue={documentSearch}
							disabled={props.disabled || !!search || !!qrUser}
							placeholder={'Buscar por documento'}
						/>
					</Box>
				)}
				{isAppUsers && (
					<Button
						variant="outlined"
						startIcon={<QrCodeIcon />}
						onClick={() => setShowQrScanner(true)}
						disabled={props.disabled || !!search || !!documentUser}
						sx={{ minWidth: '150px' }}
					>
						Escanear QR
					</Button>
				)}
			</Box>

			{/* Card Holder ID edition is only present in dev and staging */}
			<CardHolderId onClose={() => setCardHolderIdEdition(null)} username={cardHolderIdEdition?.username} />

			{userBalance?.username && <UserBalance onClose={() => setUserBalance(null)} username={userBalance?.username} />}

			{showSelectUserSectionModal && (
				<SelectUserSectionModal onClose={closeSelectUserSectionModal} user={showSelectUserSectionModal} />
			)}

			{showQrScanner && (
				<QrScanModal
					onClose={onQrCodeScan}
					title="Escanear QR de Usuario"
					description={<p>Escanea el código QR de identificación del usuario para buscar su perfil</p>}
				/>
			)}

			{userToToggleStatus && (
				<ConfirmationDialog
					title={userToToggleStatus.status === 'inactive' ? 'Confirmar activación' : 'Confirmar inactivación'}
					description={
						userToToggleStatus.status === 'inactive'
							? '¿Estás seguro que deseas activar este usuario?'
							: '¿Estás seguro que deseas inactivar este usuario?'
					}
					confirmButton={userToToggleStatus.status === 'inactive' ? 'Activar' : 'Inactivar'}
					onClose={onToggleStatusConfirm}
				/>
			)}

			{qrUser && (
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Typography variant="body1">
						Mostrando usuario escaneado:{' '}
						<strong>{`${qrUser.first_name || ''} ${qrUser.last_name || ''} (${qrUser.email})`}</strong>
					</Typography>
					<Tooltip title="Cerrar búsqueda por QR">
						<IconButton onClick={() => setQrUser(null)}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			)}

			{isMobile && (
				<TablePagination
					component="div"
					labelRowsPerPage="L/Página"
					count={documentUser || qrUser ? 1 : usersData?.total || 0}
					page={documentUser || qrUser ? 0 : page}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={rowsPerPageOptions}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					sx={{
						display: 'flex',
						height: '520px',
						justifyContent: 'flex-start',
						paddingLeft: 'unset !important',
						'& .MuiTablePagination-toolbar': {
							paddingLeft: 'unset !important',
						},
					}}
					variant="head"
				/>
			)}

			<ResponsiveTable
				elements={isMobile ? users : users}
				listSx={{ overflow: 'auto' }}
				tableSx={{ overflow: 'auto' }}
				list={{
					primaryKey: '_id',
					// avatar: <Avatar />,
					renderListItemText: (user) => {
						return (
							<Box sx={{ width: '100%' }}>
								<ListItemText
									onClick={() => onEdit(user)}
									primary={`${user.first_name || ''} ${user.last_name || ''} ${
										isPanelUser(user) ? `(${user.username})` : ''
									}`}
									secondary={
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
											{!isPanelUser(user) && <span>{user.username}</span>}
											<address>
												<a href={`mailto:${user.email}`} target="_blank" rel="noreferrer">
													{user.email}
												</a>
											</address>
											{renderUserStatus(user)}
											{isAppUsers && (
												<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
														<span>Última compra:</span>
														<UserLastOrderValue
															username={user.username}
															showAction={false}
															refreshKey={user.username === lastOrderRefreshUser ? lastOrderRefreshKey : null}
														/>
													</Box>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
														<span>Estado billetera:</span>
														{user.username === fetchWalletStatus ? (
															<UserWalletStatusValue username={user.username} />
														) : (
															<span>- - -</span>
														)}
													</Box>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
														<span>REF ID:</span>
														{user.username === fetchRefID ? <UserRefIdValue username={user.username} /> : '- - -'}
													</Box>
												</Box>
											)}
										</Box>
									}
								/>
								{isMobile && renderUserActions(user, 'row', true)}
							</Box>
						);
					},
				}}
				table={{
					renderHead: () => (
						<TableHead>
							<TableRow>
								<TableCell align="left" padding="normal">
									Nombre de usuario
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:firstName')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:lastName')}
								</TableCell>
								{!isAppUsers && (
									<TableCell align="left" padding="normal">
										{t('common:role')}
									</TableCell>
								)}
								<TableCell align="left" padding="normal">
									{t('common:email')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:status')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:loginDate')}
								</TableCell>
								{isAppUsers && (
									<TableCell align="left" padding="normal">
										Última compra
									</TableCell>
								)}
								{isAppUsers && (
									<TableCell align="left" padding="normal">
										Estado Billetera
									</TableCell>
								)}
								{isAppUsers && (
									<TableCell align="left" padding="normal" sx={{ maxWidth: 400, width: 400 }}>
										REF ID
									</TableCell>
								)}
								<TableCell align="left" padding="normal">
									{t('common:actions')}
								</TableCell>
							</TableRow>
						</TableHead>
					),
					renderRow: (user) => {
						return (
							<TableRow tabIndex={-1} key={user._id}>
								<TableCell sx={{ width: '250px', wordBreak: 'break-word' }}>{user.username}</TableCell>
								<TableCell>{user.first_name}</TableCell>
								<TableCell>{user.last_name}</TableCell>
								{!isAppUsers && <TableCell>{t(`common:role_${user.type}`)}</TableCell>}
								<TableCell>
									<address>
										<a href={`mailto:${user.email}`} target="_blank" rel="noreferrer">
											{user.email}
										</a>
									</address>
								</TableCell>

								<TableCell>{renderUserStatus(user)}</TableCell>

								<TableCell>{user.last_login ? moment(user.last_login).format('DD/MM/YYYY HH:mm:ss') : ''}</TableCell>

								{isAppUsers && (
									<TableCell>
										<UserLastOrderValue
											username={user.username}
											showAction
											refreshKey={user.username === lastOrderRefreshUser ? lastOrderRefreshKey : null}
										/>
									</TableCell>
								)}

								{isAppUsers && (
									<TableCell>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											{user.username === fetchWalletStatus ? <UserWalletStatusValue username={user.username} /> : ''}
											<Tooltip title="Actualizar estado billetera">
												<span>
													<IconButton onClick={() => setFetchWalletStatus(user.username)} size="small">
														<RefreshIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
									</TableCell>
								)}

								{isAppUsers && (
									<TableCell sx={{ maxWidth: 400, wordBreak: 'break-word' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											{user.username === fetchRefID ? <UserRefIdValue username={user.username} /> : ''}
											<Tooltip title="Actualizar REF ID">
												<span>
													<IconButton onClick={() => setFetchRefID(user.username)} size="small">
														<RefreshIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
									</TableCell>
								)}

								<TableCell>{renderUserActions(user, 'row')}</TableCell>
							</TableRow>
						);
					},
					pagination: {
						count: documentUser || qrUser ? 1 : usersData?.total || 0,
						page: documentUser || qrUser ? 0 : page,
						rowsPerPage: rowsPerPage,
						rowsPerPageOptions,
						onPageChange: handleChangePage,
						onRowsPerPageChange: handleChangeRowsPerPage,
					},
				}}
			/>
		</FullLayout>
	);
}

type UserStatusProps = {
	user: User;
	refetch?: () => any;
};

function UserStatus({ user, refetch }: UserStatusProps) {
	const { t } = useTranslation();
	const { classes } = useStyles();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);

	const send = useCallback(async () => {
		try {
			setLoading(true);
			await inviteUsers(user);
			setLoading(false);
			setError(false);
		} catch (error) {
			setError(true);
		}
		setLoading(false);
		refetch?.();
	}, [refetch, user]);

	if (loading) {
		return <CircularProgress />;
	}
	if (user.status === 'invited') {
		return (
			<Box>
				<span className={classes.status}>{t(`common:${user.status}`)}</span>
				<Button onClick={send} variant="outlined">
					{t(`common:update_${error ? 'again' : user.status}`)}
				</Button>
			</Box>
		);
	}
	return <span className={classes.status}>{t(`common:${user.status}`)}</span>;
}

function UserLastOrderValue({
	username,
	showAction,
	refreshKey,
}: {
	username: string;
	showAction?: boolean;
	refreshKey?: number | null;
}) {
	const { loading, date, refetch } = useUserLastOrder(username, true, refreshKey ?? null);

	return (
		<>
			{loading && <CircularProgress size={16} />}
			{!loading && <span>{date ? moment(date).format('DD/MM/YYYY HH:mm:ss') : ''}</span>}
			{showAction && (
				<Tooltip title="Actualizar última compra">
					<span>
						<IconButton size="small" onClick={refetch} disabled={loading}>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			)}
		</>
	);
}

function UserWalletStatusValue({ username }: { username: string }) {
	const { loading, status } = useUserWalletStatus(username);

	return (
		<>
			{loading && <CircularProgress size={16} />}
			{!loading && <span>{getWalletStatusText(status)}</span>}
		</>
	);
}

function UserRefIdValue({ username }: { username: string }) {
	const { loading, refId } = useUserRefId(username);

	return (
		<>
			{loading && <CircularProgress size={16} />}
			{!loading && (
				<Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
					{refId || ''}
				</Typography>
			)}
		</>
	);
}

function getWalletStatusText(status?: OnBoardingStatus['status'] | null) {
	switch (status) {
		case 'ok':
			return 'Realizado';
		case 'error':
			return 'Con error';
		default:
			return 'Pendiente';
	}
}

const useStyles = makeStyles()((theme) => ({
	status: {
		marginRight: theme.spacing(1),
	},
}));

function isPanelUser(user: User): boolean {
	return user.type === UsersTypes.admin || user.type === UsersTypes.panel;
}
